/**
 * The single place any `import.meta.env` value is read.
 *
 * Two reasons it is centralised:
 *
 *  1. CLAUDE.md rule 5 — no hardcoded tenant id, client id or API URL. Every
 *     one of them arrives as a VITE_* variable, documented in .env.example.
 *     Grep for `import.meta.env` and this file should be the only hit.
 *
 *  2. Misconfiguration must fail loudly at startup, not silently at the first
 *     sign-in attempt. A missing client id produces a blank Entra error page
 *     that costs an hour to diagnose; `assertConfigured()` produces a list of
 *     the variables that are missing.
 *
 * Everything here is public by definition — Vite inlines VITE_* values into
 * the shipped bundle. Nothing secret may be added.
 */

import type { ClmRoleName, RoleGroupBinding } from './auth/claims';

export type AppEnvLabel = 'local' | 'dev' | 'test' | 'uat' | 'prod';

export interface WebConfig {
  readonly entra_tenant_id: string;
  readonly entra_client_id: string;
  readonly entra_authority: string;
  readonly redirect_uri: string;
  readonly post_logout_redirect_uri: string;
  /** OAuth scope for the gateway API. Not CLM party scope — see NFR-06. */
  readonly api_scope: string;
  /** Origin of the gateway API. The app's only egress target (rule 11). */
  readonly api_base_url: string;
  readonly app_env: AppEnvLabel;
  /**
   * CLM-65 — the Entra security groups that grant each CLM role, from the four
   * OPTIONAL `VITE_ENTRA_GROUP_CLM_*` variables (the same object ids the gateway
   * reads as CLM_AUTH_GROUP_*). Only the configured ones are listed. Used for
   * the identity footer's role tags and for nothing else: display, never
   * authorisation (NFR-06). Group object ids are public, like every VITE_ value.
   */
  readonly role_groups: readonly RoleGroupBinding[];
}

const raw = import.meta.env;

function optional(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? undefined : trimmed;
}

function required(value: string | undefined): string {
  // Deliberately returns '' rather than throwing: assertConfigured() reports
  // every missing variable at once, which is far more useful than failing on
  // whichever one happens to be read first.
  return optional(value) ?? '';
}

const tenantId = required(raw.VITE_ENTRA_TENANT_ID);
const origin = typeof window === 'undefined' ? '' : window.location.origin;

const APP_ENV_LABELS: readonly AppEnvLabel[] = ['local', 'dev', 'test', 'uat', 'prod'];

function appEnv(): AppEnvLabel {
  const value = optional(raw.VITE_APP_ENV);
  return APP_ENV_LABELS.find((label) => label === value) ?? 'local';
}

const ROLE_GROUP_VARS: readonly (readonly [role: ClmRoleName, value: string | undefined])[] = [
  ['admin', raw.VITE_ENTRA_GROUP_CLM_ADMIN],
  ['contributor', raw.VITE_ENTRA_GROUP_CLM_CONTRIBUTOR],
  ['reader', raw.VITE_ENTRA_GROUP_CLM_READER],
  ['legal_reviewer', raw.VITE_ENTRA_GROUP_CLM_LEGAL],
];

function roleGroups(): readonly RoleGroupBinding[] {
  const bindings: RoleGroupBinding[] = [];
  for (const [role, value] of ROLE_GROUP_VARS) {
    const group_id = optional(value);
    if (group_id !== undefined) bindings.push({ role, group_id });
  }
  return bindings;
}

export const config: WebConfig = {
  entra_tenant_id: tenantId,
  entra_client_id: required(raw.VITE_ENTRA_CLIENT_ID),
  entra_authority:
    optional(raw.VITE_ENTRA_AUTHORITY) ?? `https://login.microsoftonline.com/${tenantId}`,
  redirect_uri: optional(raw.VITE_REDIRECT_URI) ?? origin,
  post_logout_redirect_uri: optional(raw.VITE_POST_LOGOUT_REDIRECT_URI) ?? origin,
  api_scope: required(raw.VITE_API_SCOPE),
  // Trailing slash stripped once, here, so no caller has to think about it.
  api_base_url: required(raw.VITE_API_BASE_URL).replace(/\/+$/, ''),
  app_env: appEnv(),
  role_groups: roleGroups(),
};

const REQUIRED_VARS: readonly (readonly [name: string, value: string])[] = [
  ['VITE_ENTRA_TENANT_ID', config.entra_tenant_id],
  ['VITE_ENTRA_CLIENT_ID', config.entra_client_id],
  ['VITE_API_SCOPE', config.api_scope],
  ['VITE_API_BASE_URL', config.api_base_url],
];

/** Names of required VITE_* variables that are absent or blank. */
export function missingConfig(): readonly string[] {
  return REQUIRED_VARS.filter(([, value]) => value === '').map(([name]) => name);
}

export function isConfigured(): boolean {
  return missingConfig().length === 0;
}
